import { computed, Ref, watchEffect, onMounted, ref } from '@vue/composition-api'

type Emit = (event: string, ...args: any[]) => void;

interface InputProps {
    readonly: boolean | string;
    disabled: boolean | string;
    error: any;
    showReset?: boolean | string;
    actionSequentialFocus?: boolean | string;
}

export function useDefaultInputFlags(props: InputProps) {
    return {
        isReadOnly: computed(() => props.readonly.toString() == 'true'),
        isDisabled: computed(() => props.disabled.toString() == 'true'),
        resetActionVisible: computed(() => props.showReset && props.showReset.toString() == 'true'),
        actionSequentialFocusActive: computed(() => props.actionSequentialFocus && props.actionSequentialFocus.toString() == 'true'),
    }
}

export function useWrapperClasses(props: InputProps, attrs: Record<string,string>) {
    // If given convert the column span to fraction.
    let span = attrs.span;
    if (span) {
        /* Expected span classes :

            wok--w-1/12
            wok--w-2/12
            wok--w-3/12
            wok--w-4/12
            wok--w-5/12
            wok--w-6/12
            wok--w-7/12
            wok--w-8/12
            wok--w-9/12
            wok--w-10/12
            wok--w-11/12
        */
        if (/^(\d|10|11)$/.test(span)) {
            span = `wok--w-${span}/12`;
        }
        else {
            span = 'wok--w-full';
        }
    }

    return computed(() => ({
        'wok--form-item': true,
        'wok--body-short-01': true,
        'wok--input--disabled': props.disabled.toString() == 'true',
        'wok--input--readonly': props.readonly.toString() == 'true',
        'wok--input--error': props.error != null,
        'wok--input--span': span != null && span != '',
        [span]: span != null && span != ''
    }));
}

export function useInputClasses(type: string, hasActions?: Ref<boolean>) {
    return computed(() => ({
        [`wok--input`]: true,
        [`wok--input-${type}`]: true,
        [`wok--input-${type}--with-actions`]: hasActions && hasActions.value || false
    }));
}

interface FocusProps {
    focus: boolean | undefined;
}

export function useExposeFocus(inputElement: Ref<HTMLInputElement>, emit: Emit, props: FocusProps) {
    if (props.focus != null) {
        watchEffect(() => {
            if (inputElement.value != null) {
                if (props.focus === true) {
                    inputElement.value.focus();
                }
                else if (props.focus === false) {
                    inputElement.value.blur();
                }
            }
        });
        onMounted(() => {
            if (inputElement.value != null && props.focus === true) {
                inputElement.value.focus();
            }
        });
    }

    const focused = ref(false);

    const onFocus = () => {
        focused.value = true;
        if (props.focus != null) {
            emit('focus');
        }
    }

    const onBlur = () => {
        focused.value = false;
        if (props.focus != null) {
            emit('blur');
        }
    }

    return { focused, onFocus, onBlur }
}
